import { FC, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Icon, IconButton } from '@kontentino/ui';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { useTranslation } from 'react-i18next';
import type { ReportPreviewPayload } from 'api/analytics-reports/types';
import AnalyticsExportPageRenderer from './AnalyticsExportPageRenderer';
import { AnalyticsExportThemeProvider } from './analyticsExportTheme';
import { ANALYTICS_EXPORT_ROOT_CLASS_NAME } from './constants/analyticsExportPageLayout';
import { useAnalyticsExportPages } from './useAnalyticsExportPages';
import styles from './AnalyticsExportPresentation.module.scss';

interface Props {
  preview: ReportPreviewPayload;
  /** 1-based slide to open on, so a deck can be shared at a given page. */
  initialSlide?: number | null;
}

/**
 * The export as a full-screen deck. Same pages as the PDF, dark theme — on
 * screen nothing is printed, so the gradient can run edge to edge.
 */
const AnalyticsExportPresentation: FC<Props> = ({ preview, initialSlide }) => {
  const { t } = useTranslation();
  const pages = useAnalyticsExportPages(preview);
  const [requestedIndex, setRequestedIndex] = useState(() =>
    initialSlide != null && initialSlide > 0 ? Math.floor(initialSlide) - 1 : 0,
  );
  const lastIndex = pages.length - 1;
  // A deep link can name a slide that does not exist, or a fraction; without
  // clamping no slide gets the active class and the deck renders empty.
  const activeIndex = Math.min(
    Math.max(Math.trunc(requestedIndex), 0),
    Math.max(lastIndex, 0),
  );

  const goTo = useCallback(
    (index: number) => {
      setRequestedIndex(Math.min(Math.max(index, 0), Math.max(lastIndex, 0)));
    },
    [lastIndex],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Space and Enter belong to whatever control has focus — swallowing them
      // here would stop the progress buttons from activating. Arrows do nothing
      // on a button, so the deck keeps those.
      const target = event.target as HTMLElement | null;
      const isOnControl = target?.closest('a, button, input, select, textarea');

      if (isOnControl && (event.key === ' ' || event.key === 'Enter')) {
        return;
      }

      switch (event.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          event.preventDefault();
          setRequestedIndex((current) => Math.min(current + 1, lastIndex));
          break;
        case 'ArrowLeft':
        case 'PageUp':
          event.preventDefault();
          setRequestedIndex((current) => Math.max(current - 1, 0));
          break;
        case 'Home':
          setRequestedIndex(0);
          break;
        case 'End':
          setRequestedIndex(lastIndex);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lastIndex]);

  if (pages.length === 0) {
    return (
      <div className="tw-flex tw-h-screen tw-items-center tw-justify-center tw-bg-gradient-ai-1">
        <p className="tw-text-lg tw-text-white/80">
          {t('analyticsReports.viewModal.noMetricsConfigured')}
        </p>
      </div>
    );
  }

  return (
    <AnalyticsExportThemeProvider theme="dark">
      <div
        className={clsx(
          ANALYTICS_EXPORT_ROOT_CLASS_NAME,
          'tw-relative tw-h-screen tw-w-screen tw-overflow-hidden tw-bg-gradient-ai-1',
        )}
        data-cy="analytics_export-public_presentation"
      >
        {pages.map((page, index) => (
          <div
            key={page.key}
            aria-hidden={index !== activeIndex}
            // Keeps a hidden slide's footer link out of the tab order; see the
            // stylesheet for why `visibility` rather than `inert`.
            className={clsx(
              styles.slide,
              'tw-absolute tw-inset-0 tw-flex tw-flex-col',
              index === activeIndex && styles.active,
              index < activeIndex && styles.past,
            )}
          >
            <AnalyticsExportPageRenderer
              page={page}
              pageNumber={index + 1}
              totalPages={pages.length}
            />
          </div>
        ))}

        <div className="tw-absolute tw-inset-x-0 tw-bottom-6 tw-flex tw-items-center tw-justify-center tw-gap-4">
          <IconButton
            variant="ghost"
            size="small"
            className="tw-text-white"
            icon={<Icon icon={faChevronLeft} />}
            aria-label={t('analyticsExports.presentationPreviousSlide')}
            onClick={() => goTo(activeIndex - 1)}
            disabled={activeIndex === 0}
            data-cy="analytics_export-presentation_prev"
          />
          <div className="tw-flex tw-items-center tw-gap-2">
            {pages.map((page, index) => (
              <button
                key={page.key}
                type="button"
                aria-label={page.title}
                onClick={() => goTo(index)}
                className={clsx(
                  'tw-h-2 tw-rounded-full tw-transition-all tw-duration-300',
                  index === activeIndex
                    ? 'tw-w-6 tw-bg-white'
                    : 'tw-w-2 tw-bg-white/40',
                )}
              />
            ))}
          </div>
          <IconButton
            variant="ghost"
            size="small"
            className="tw-text-white"
            icon={<Icon icon={faChevronRight} />}
            aria-label={t('analyticsExports.presentationNextSlide')}
            onClick={() => goTo(activeIndex + 1)}
            disabled={activeIndex === lastIndex}
            data-cy="analytics_export-presentation_next"
          />
        </div>
      </div>
    </AnalyticsExportThemeProvider>
  );
};

export default AnalyticsExportPresentation;
